import {
  createContext,
  useState,
  useCallback,
  useContext,
  type ReactNode
} from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Snackbar from '@mui/material/Snackbar';

interface ToastOptions {
  message?: string;
  title?: string;
  severity?: 'success' | 'info' | 'warning' | 'error';
  variant?: 'success' | 'info' | 'warning' | 'error';
  autoDismiss?: number;
}

interface Toast extends ToastOptions {
  key: number;
}

const ToastContext = createContext<((opts: ToastOptions) => void) | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast | null>(null);

  const enqueueToast = useCallback(
    ({
      message,
      title,
      variant,
      severity,
      autoDismiss = 4000
    }: ToastOptions = {}) => {
      setToast({
        message,
        title,
        severity: severity || variant,
        autoDismiss,
        key: Date.now()
      });
    },
    []
  );

  return (
    <ToastContext.Provider value={enqueueToast}>
      {children}
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={toast?.autoDismiss ?? 4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        key={toast?.key}
      >
        <Alert
          onClose={() => setToast(null)}
          severity={toast?.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {toast?.title && <AlertTitle>{toast.title}</AlertTitle>}
          {toast?.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToastProvider() {
  const enqueueToast = useContext(ToastContext);
  if (!enqueueToast) {
    return {
      enqueueToast: ({
        message,
        title,
        severity,
        variant
      }: ToastOptions = {}) => {
        const text = title || message;
        if (text)
          console[
            severity === 'error' || variant === 'error' ? 'error' : 'log'
          ](text);
      }
    };
  }
  return { enqueueToast };
}
